import _ from 'lodash'
import { fetchData } from '../../client'
import sanity from '../../contexts/SanityClient'
import {
  GetMessageDocument,
  GetMessageQuery,
  GetMessageQueryVariables,
  GetMessagesDocument,
  GetMessagesQuery,
  GetMessagesQueryVariables,
  HasUnreadMessageDocument,
  HasUnreadMessageQuery,
  HasUnreadMessageQueryVariables,
  LikeMessageDocument,
  LikeMessageMutation,
  LikeMessageMutationVariables,
  PostTeamMessageDocument,
  PostTeamMessageMutation,
  PostTeamMessageMutationVariables,
  ReadMessageDocument,
  ReadMessageMutation,
  ReadMessageMutationVariables,
  UnlikeMessageDocument,
  UnlikeMessageMutation,
  UnlikeMessageMutationVariables,
} from '../../generated/api'
import {
  Message,
  MessageWithContent,
  SanityMessage,
  TeamMessage,
} from './types'

export async function likeMessage({ campaignId }: { campaignId: string }) {
  await fetchData<LikeMessageMutation, LikeMessageMutationVariables>(
    LikeMessageDocument,
    {
      campaignId,
    }
  )
}

export async function unlikeMessage({ campaignId }: { campaignId: string }) {
  await fetchData<UnlikeMessageMutation, UnlikeMessageMutationVariables>(
    UnlikeMessageDocument,
    {
      campaignId,
    }
  )
}

export async function readMessage({ messageId }: { messageId: string }) {
  await fetchData<ReadMessageMutation, ReadMessageMutationVariables>(
    ReadMessageDocument,
    {
      messageId,
    }
  )
}

export async function fetchMessages({
  category,
}: {
  category?: string
}): Promise<Message[]> {
  const response = await fetchData<GetMessagesQuery, GetMessagesQueryVariables>(
    GetMessagesDocument,
    { category }
  )
  return response.getMessages as Message[]
}

export async function fetchMessage({
  messageId,
}: {
  messageId: string
}): Promise<Message | TeamMessage> {
  const response = await fetchData<GetMessageQuery, GetMessageQueryVariables>(
    GetMessageDocument,
    {
      messageId,
    }
  )
  return response.getMessage as Message
}

export async function hasUnreadMessage(): Promise<boolean> {
  return (
    await fetchData<HasUnreadMessageQuery, HasUnreadMessageQueryVariables>(
      HasUnreadMessageDocument
    )
  ).hasUnreadMessage
}

export async function postTeamMessage({
  title,
  body,
}: {
  title: string
  body: string
}): Promise<void> {
  await fetchData<PostTeamMessageMutation, PostTeamMessageMutationVariables>(
    PostTeamMessageDocument,
    {
      content: { title, body },
    }
  )
}

const ASSET_QUERY: string =
  'asset->{ url, "dimensions": metadata.dimensions{ height, width } }' as const
const LINK_QUERY: string = '..., event->{ _ref, eventSlug }' as const
const sanityQuery: string =
  `*[_type == "message" && _id in $ids]{ ..., category->{ ..., icon{${ASSET_QUERY}}, image{${ASSET_QUERY}} }, image{${ASSET_QUERY}}, link1{${LINK_QUERY}}, link2{${LINK_QUERY}} }` as const

export async function fillSanityContent(
  messages: Message[]
): Promise<MessageWithContent[]> {
  const ids = messages.map((message) => message.content)
  if (ids.length === 0) return []

  const content = await sanity
    .withConfig({ useCdn: true })
    .fetch<SanityMessage[]>(sanityQuery, { ids })

  return messages
    .map((message) => {
      const sanity = _.find(content, { _id: message.content })
      if (!sanity) return null
      return { ...message, sanity } as MessageWithContent
    })
    .filter((message) => !!message) as MessageWithContent[]
}
